import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MagazineCoverUploader from '../../MagazineCoverUploader';
import MagazineFieldEditor from './MagazineFieldEditor';
import { PREVIOUS_ISSUE_FIELDS } from './levelConfig';
import {
  formatSaturdayLabel,
  resolveReleaseDate,
  suggestedCoverFilename
} from './releaseDate';
import { useSuperDuper } from '../../context/SuperDuperContext';

/**
 * One previous issue card. `displayIdx` is the newest-first position used for
 * ordering, while `actualIdx` is the real array index used for every mutation.
 * Labels and suggested cover filenames use the Saturday releaseDate.
 */
const MagazinePreviousIssue = ({ config, issue, actualIdx, previousLength }) => {
  const {
    magazinesLoading,
    editingField,
    tempValue,
    setTempValue,
    handleEditField,
    handleSaveField,
    handleCancelMagazineEdit,
    handleClearField,
    handleCoverUploadSuccess,
    handleRemovePreviousIssue,
    handleCopyToClipboard
  } = useSuperDuper();

  const { level } = config;
  const releaseDate = resolveReleaseDate(issue, actualIdx, previousLength);
  const dateLabel = formatSaturdayLabel(releaseDate);

  return (
    <Box sx={{ mb: 3, p: 2, backgroundColor: '#a3a5ee33', borderRadius: 1, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: config.issueLabelColor, fontWeight: 600 }}>
          {dateLabel}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleRemovePreviousIssue(level, actualIdx)}
          disabled={magazinesLoading}
          sx={{ minWidth: '100px' }}
        >
          Remove
        </Button>
      </Box>

      {/* Title and Link1 fields */}
      {PREVIOUS_ISSUE_FIELDS.map((field) => (
        <MagazineFieldEditor
          key={field}
          label={field}
          capitalizeLabel
          labelColor={config.fieldLabelColor}
          isEditing={
            editingField?.level === level &&
            editingField?.section === 'previous' &&
            editingField?.field === field &&
            editingField?.index === actualIdx
          }
          value={issue[field]}
          tempValue={tempValue}
          setTempValue={setTempValue}
          rows={2}
          inputSx={config.fieldInputSx}
          displayBg="rgb(45, 45, 45)"
          displayMinHeight="50px"
          labelledActions={config.labelledFieldActions}
          disabled={magazinesLoading}
          placeholder={field === 'title' ? dateLabel : undefined}
          onSave={handleSaveField}
          onCancel={handleCancelMagazineEdit}
          onEdit={() => {
            handleEditField(level, 'previous', field, actualIdx);
            // Auto-fill empty title with the Saturday label so Save persists it
            if (field === 'title' && !issue.title) {
              setTempValue(dateLabel);
            }
          }}
          onCopy={() => handleCopyToClipboard(issue[field] || '')}
          onClear={() => handleClearField(level, 'previous', field, actualIdx)}
        />
      ))}

      {/* Cover1 Upload */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
        <MagazineCoverUploader
          label={`Cover · ${dateLabel}`}
          suggestedFilename={suggestedCoverFilename(releaseDate, level)}
          currentUrl={issue.cover1}
          onUploadSuccess={(url) => handleCoverUploadSuccess(level, 'previous', 'cover1', actualIdx, url)}
        />
      </Paper>
    </Box>
  );
};

export default MagazinePreviousIssue;
